<!--
// nuxt-ui/pages/[region]/SquadSimulator.vue
-->
<script setup lang="ts" xmlns="http://www.w3.org/1999/html">
import type { Player } from "~/src/player/domain/Player";
import type { Team } from "~/src/team/domain/models/Team";
import type { AnyDraftPlayer, DraftPlayerRole } from "~/src/draft/domain/models/DraftPlayer";
import type { MacroFilter } from "~/src/draft/domain/models/Filters";
import { useRegions } from "~/nuxt-ui/Shared/composables/regions";
import { useSquadSimulator } from "~/nuxt-ui/Shared/composables/squadSimulator";
import { useWebContext } from "~/nuxt-ui/Shared/composables/webContext";
import { orderTeamsAlphabetically } from "~/src/team/domain/services/teamService";
import { useCompetitionStore } from "~/nuxt-ui/stores/competitionStore";
import { scrollToTop, slugify } from "~/src/Shared/utils";
import { initDropdowns } from "flowbite";
import CardTeamSquadSimulator from "~/nuxt-ui/components/pages/region-squad-simulator/CardTeamSquadSimulator.vue";
import CardPlayerSquadSimulator from "~/nuxt-ui/components/pages/region-squad-simulator/CardPlayerSquadSimulator.vue";
import BaseModal from "~/nuxt-ui/components/base/BaseModal.vue";
import ResetIcon from "~/nuxt-ui/components/Shared/icons/ResetIcon.vue";
import DownloadIcon from "~/nuxt-ui/components/Shared/icons/DownloadIcon.vue";
import BaseSpinner from "~/nuxt-ui/components/base/BaseSpinner.vue";
import HeaderTitleCentered from "~/nuxt-ui/components/Shared/HeaderTitleCentered.vue";
import { useSeasons } from "~/nuxt-ui/Shared/composables/season/seasons";
import { useDraftPlayers } from "~/nuxt-ui/Shared/composables/draftPlayers";
import CardPlayerTeamSimulatorDraft from "~/nuxt-ui/components/pages/region-squad-simulator/CardPlayerTeamSimulatorDraft.vue";
import { useImages } from "~/nuxt-ui/Shared/composables/images";
import SearchField from "~/nuxt-ui/components/Shared/SearchField.vue";
import BaseRangeSlider from "~/nuxt-ui/components/base/BaseRangeSlider.vue";
import draftService from "~/src/draft/domain/services/draftService";
import LeftArrow from "~/nuxt-ui/components/Shared/icons/LeftArrow.vue";

type Filters = {
  searchTerm: string | null;
  role: DraftPlayerRole | null;
  average: MacroFilter;
};

const { t } = useI18n();
const localePath = useLocalePath();
const { region } = useCompetitionStore();
const { getSeasonTeams } = useSeasons();
const { isSpain, isAmericas, isWorldCup } = useRegions();
const { isKings, isQueens, contextSiteTitle, contextDomain } = useWebContext();
const { playerPlaceholderPath } = useImages();
const { getDraftPlayers } = useDraftPlayers();
const { MAX_PLAYERS, isMaxPlayers, isPlayerMinLimitReached, isPlayerMaxLimitReached } = useSquadSimulator();

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/team-simulator",
    es: "/[region]/simulador-equipo",
    de: "/[region]/team-simulator",
    fr: "/[region]/team-simulator",
    it: "/[region]/team-simulator",
    pt: "/[region]/team-simulator",
  },
});

definePageMeta({
  middleware: [
    "not-found-italy",
    "not-found-americas",
    "not-found-spain",
    "not-found-world-cup",
    "not-found-nations",
    "not-found-queens",
    "not-found-king-of-kings-nations",
    "not-found-germany",
    "not-found-france",
  ],
});

useHead({
  script: [
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/dom-to-image/2.6.0/dom-to-image.min.js",
      tagPosition: "head",
      tagPriority: "high",
    },
  ],
});

const selectedTeam = ref<Team | undefined>();

const modifiedTeamPlayers = ref<AnyDraftPlayer[]>([]);

const isDownloading = ref<boolean>(false);
const showModal = ref<boolean>(false);
const isPortrait = ref<boolean>(false);
const isLimitReached = ref<boolean>(false);

const draftPlayers = ref<AnyDraftPlayer[]>();
const selectedTeamCardsContainer = ref<HTMLElement | null>(null);
const swingPlayerCards = ref<boolean>(false);

const modalInfo = ref<{ visible: boolean; message: string }>({ visible: false, message: "" });

const placeholderPlayer = {
  id: -1,
  shortName: undefined,
  role: undefined,
  gender: "male", //male or female
  countryId: 0,
  jersey: "",
  birthDate: "1900-01-01T00:00:00Z",
  height: null,
  image: {
    url: playerPlaceholderPath,
  },
  metaInformation: null,
  category: "draft",
  marketValue: 0,
};

const playerPositions = [
  { positionId: "DF", position: "draft.player.position.DF" },
  { positionId: "MC", position: "draft.player.position.MC" },
  { positionId: "DL", position: "draft.player.position.DL" },
  { positionId: "PT", position: "draft.player.position.PT" },
];

const seasonIdToUse: number = 33;
const seasonTeams = await getSeasonTeams(seasonIdToUse);
const activeTeams: Team[] = seasonTeams.filter(t => !t.isPlaceholder && t.completeName !== "TBD");

const downloadPlayers = computed((): Player[] => {
  return modifiedTeamPlayers.value.filter(p => p.id > 0);
});

const leagueTitle = region?.leagueTitle || contextSiteTitle;
const currentImage = region?.footerLogo || undefined;

const restart = () => {
  selectedTeam.value = undefined;
  modifiedTeamPlayers.value = [];
  draftPlayers.value = [];
  isLimitReached.value = false;

  scrollToTop(200);
};

const showAlert = (message: string): void => {
  modalInfo.value = { visible: true, message };
};

const setDraftPlayersToRecruit = async () => {
  const players = await getDraftPlayers(19, "player");
  const goalkeepers = await getDraftPlayers(19, "goalkeeper");

  draftPlayers.value = [...players, ...goalkeepers];
};

const { findMacroMinMax } = draftService();
const filtersKey = ref(0);
const filters = reactive<Filters>({
  searchTerm: null,
  role: null,
  average: { min: 0, max: 0 },
});

const averageMinComp = computed({
  get() {
    return filters.average?.min || 0;
  },
  set(newValue) {
    filters.average.min = Number(newValue);
  },
});

const averageMaxComp = computed({
  get() {
    return filters.average?.max || 100;
  },
  set(newValue) {
    filters.average.max = Number(newValue);
  },
});

const handleRecruitPlayer = (player: AnyDraftPlayer) => {
  if (modifiedTeamPlayers.value.some(p => p.id === player.id)) {
    modifiedTeamPlayers.value = modifiedTeamPlayers.value.filter(p => p.id !== player.id);
  } else {
    modifiedTeamPlayers.value.unshift(player);
    if (modifiedTeamPlayers.value.length > MAX_PLAYERS) {
      showAlert(t("squadSimulator.team.alert.maxSquadSize"));
      isLimitReached.value = true;
      modifiedTeamPlayers.value = modifiedTeamPlayers.value.filter(p => p.id !== player.id);
    }
  }
};

const updateMediaValues = players => {
  const [from, to] = findMacroMinMax("average", players);
  filters.average.min = from;
  filters.average.max = to;
  filters.average.from = from;
  filters.average.to = to;

  filtersKey.value++;
};

const resetFilters = () => {
  filters.searchTerm = null;
  filters.role = null;
  updateMediaValues(draftPlayers.value);
};

const getTeamSquad = async (): Promise<void> => {
  try {
    //Always show max spots
    modifiedTeamPlayers.value = [];

    await setDraftPlayersToRecruit();
    if (draftPlayers.value) {
      updateMediaValues(draftPlayers.value);
    }
  } catch (error) {
    console.error(`Failed to get selected team squad:`, error);
  } finally {
    initDropdowns();
  }
};

const filteredDraftPlayers = computed((): AnyDraftPlayer[] => {
  if (!draftPlayers.value) return [];

  let _players = draftPlayers.value as AnyDraftPlayer[];

  if (filters.role) {
    _players = _players.filter(p => p.role === filters.role);
  }

  if (filters.searchTerm) {
    _players = _players.filter(p => {
      const { searchTerm } = filters as string;
      return (
        slugify(p.name).includes(slugify(searchTerm)) ||
        (p.draftJersey && p.draftJersey.toString().includes(searchTerm)) ||
        (p.draftJerseyPrefix && p.draftJerseyPrefix.toString().toLowerCase().includes(searchTerm))
      );
    });
  }

  if (filters.average.to || filters.average.from) {
    _players = _players.filter(p => {
      const isAboveMin = p.macros.average >= filters.average.min;
      const isBelowMax = p.macros.average <= filters.average.max;
      return isAboveMin && isBelowMax;
    });
  }

  return _players;
});

const showDownloadButton = computed(() => {
  return (
    !isPlayerMinLimitReached(modifiedTeamPlayers.value) && !isPlayerMaxLimitReached(modifiedTeamPlayers.value)
  );
});

const getSelectedTeamSquad = async (teamId: number): Promise<void> => {
  if (window && window.innerWidth < 1280) {
    swingPlayerCards.value = true;
    setTimeout(() => {
      swingPlayerCards.value = false;
    }, 5000);
  }

  await getTeamSquad(teamId, false);
  scrollToTop(200);
};

const contextSiteTitleNoSpaces = contextSiteTitle.replace(/\s+/g, "");

const downloadTeamImage = () => {
  isDownloading.value = true;

  if (typeof window !== "undefined" && window.domtoimage) {
    const htmlSection = document.getElementById("SquadSimulatorCapture");

    if (htmlSection) {
      setTimeout(() => {
        domtoimage
          .toPng(htmlSection, {
            quality: 1,
            bgcolor: "#000",
          })
          .then(function (dataUrl) {
            const link = document.createElement("a");
            link.href = dataUrl;
            link.download = t("squadSimulator.capture.downloadName", { contextSiteTitleNoSpaces });
            link.click();
            link.remove();
            isDownloading.value = false;
            showModal.value = false;
          })
          .catch(function (error) {
            console.error("Error generating screenshot:", error);
            isDownloading.value = false;
            showModal.value = false;
          });
      }, 200);
    }
  }
};

onMounted(() => {
  initDropdowns();
});

useSeoMeta({
  title: t("meta.title.squadSimulator", { contextSiteTitle }),
  description: t("meta.description.squadSimulator", { leagueTitle }),
  ogTitle: t("meta.title.squadSimulator", { contextSiteTitle }),
  ogDescription: t("meta.description.squadSimulator", { leagueTitle }),
});

const simulatorTeamsItemList = activeTeams.map((team, index) => {
  const url = localePath({
    name: "teams-team-teamName",
    params: { team: team.id, teamName: slugify(team.name) },
  });
  return `{"@type": "ListItem","position": "${index + 1}","item": {"@type" : "SportsTeam", "name" : "${team?.name}"}}`;
});

useHead({
  script: () => [
    {
      type: "application/ld+json",
      innerHTML: `{"@context": "https://schema.org","@type": "ItemList","name": "${contextSiteTitle + " " + t("meta.title.squadSimulator")}", "itemListElement": [${simulatorTeamsItemList}]}`,
    },
  ],
});
</script>

<template>
  <header-title-centered
    :title="$t('squadSimulator.title')"
    :subtitle="$t('squadSimulator.subtitle', { contextSiteTitle })"
  ></header-title-centered>
  <section class="my-6 main-container">
    <!--    <div class="kql-w-container py-8 px-4 md:py-10 md:px-6">-->
    <!--      <h1 class="text-center text-3xl md:text-4xl font-extrabold text-gray-100 mb-6">-->
    <!--        <span class="relative inline-block"> {{}} </span>-->
    <!--      </h1>-->
    <!--    </div>-->

    <div class="flex flex-col gap-2 py-8 px-4 md:py-6 md:px-6 kql-w-container">
      <div
        id="teamsGrid"
        :class="[
          'grid grid-cols-1 xl:grid-cols-3 gap-4 ',
          selectedTeam ? 'md:grid-cols-5' : 'md:grid-cols-2',
        ]"
      >
        <transition-group name="teams">
          <card-team-squad-simulator
            v-for="(team, i) in orderTeamsAlphabetically(activeTeams)"
            :key="team.id"
            :team="team"
            :teamBudget="undefined"
            :preload="i === 0"
            with-hover
            v-model:selected-team="selectedTeam"
            @update:selected-team="e => getSelectedTeamSquad(e.id)"
          ></card-team-squad-simulator>
        </transition-group>
      </div>
      <div id="instructionButton" v-if="selectedTeam" class="text-center mt-4">
        <a href="#instructionsCard" class="btn btn-primary text-sm max-w-max"
          ><span class="bg-primary text-black rounded-full font-bold py-0.5 px-1.5 mr-2">?</span
          >{{ $t("squadSimulator.instructions.title") }}</a
        >
      </div>
    </div>

    <transition name="squad">
      <div v-show="selectedTeam" class="kql-w-container md:px-6">
        <div
          id="filters-section"
          :key="filtersKey"
          class="max-w-full my-4 md:px-12 flex flex-col justify-between items-center md:mb-7 md:gap-8 md:flex-row gap-4"
        >
          <!--          Filters for draft players-->
          <div
            class="flex px-4 md:px-0 flex-col md:flex-row items-stretch md:items-start md:justify-between gap-6 w-full md:w-1/2"
          >
            <div class="max-w-full overflow-x-scroll hide-scrollbar">
              <p class="text-xs lg:text-sm mb-4 lg:mb-6 sticky left-0">
                {{ $t("draft.player.position") }}
              </p>
              <div class="flex justify-start md:justify-stretch gap-1 lg:gap-0">
                <button
                  v-for="(position, index) in playerPositions"
                  :key="index"
                  :class="[
                    'kql-player-filter',
                    {
                      '!bg-primary !text-black': position.positionId === filters.role,
                    },
                  ]"
                  @click="filters.role = position.positionId"
                >
                  {{ $t(position.position) }}
                </button>
              </div>
            </div>

            <base-range-slider
              v-if="filters.average"
              id="overall"
              :title="$t('draft.player.average')"
              v-model:min="averageMinComp"
              v-model:max="averageMaxComp"
              :from="filters.average.from"
              :to="filters.average.to"
              class="range-average"
            ></base-range-slider>
          </div>

          <div class="px-4 md:px-0 sm:ml-auto w-full max-w-full sm:max-w-[364px]">
            <search-field v-model="filters.searchTerm"></search-field>
          </div>
        </div>
        <div class="flex items-center justify-between md:justify-start gap-4 px-4 md:gap-10 md:px-12">
          <p class="text-gray-400 text-sm">Total: {{ filteredDraftPlayers.length }}</p>
          <button
            class="text-primary border-primary border rounded-lg px-3 py-2 text-xs"
            @click="resetFilters"
          >
            {{ $t("draft.player.filters.clean") }}
          </button>
        </div>

        <!-- List of draft players -->
        <div
          class="overflow-y-scroll max-h-96 min-h-96 md:max-h-[480px] md:min-h-[480px] mt-2 md:pr-0 max-w-6xl mx-auto"
        >
          <div class="flex justify-center items-center flex-wrap gap-3" ref="draftPlayerCardsContainer">
            <card-player-team-simulator-draft
              v-for="player in filteredDraftPlayers"
              :key="player.id"
              :player="player"
              :is-selected="modifiedTeamPlayers.some(p => p.id === player.id)"
              loading-lazy
              @recruit-player="handleRecruitPlayer"
            ></card-player-team-simulator-draft>
          </div>
        </div>

        <p class="kql-page-section-title px-4 mt-6">{{ $t("squadSimulator.team.divisor.yourSquad") }}</p>
        <hr />
        <div v-if="selectedTeam" class="flex gap-2 justify-end mt-4 md:px-12">
          <button
            class="btn btn-default flex gap-2 items-center justify-center md:justify-start w-full sm:w-auto"
            @click="restart"
          >
            <span class="ml-2"> <ResetIcon /> </span>
            <span class="hidden md:inline-block">{{ t("squadSimulator.button.restart") }}</span>
          </button>

          <!-- Modal for Orientation Preview -->
          <button
            class="btn flex gap-2 items-center justify-center w-full sm:w-auto"
            :class="!showDownloadButton ? 'btn-disabled' : 'btn-emphasis'"
            :disabled="!showDownloadButton"
            @click="showModal = true"
          >
            <span class="ml-2"> <DownloadIcon /> </span>
            <span class="hidden md:inline-block">{{ t("squadSimulator.button.download") }}</span>
          </button>
        </div>
        <div class="overflow-x-scroll hide-scrollbar pl-4 mt-4">
          <div
            ref="selectedTeamCardsContainer"
            :class="[
              'grid grid-cols-6 min-w-max justify-items-center gap-y-2 gap-x-2 pb-6 md:px-12',
              { swing: swingPlayerCards },
            ]"
          >
            <!-- Selected Team Players -->
            <card-player-squad-simulator
              v-for="(player, index) in modifiedTeamPlayers"
              :key="player.id"
              :player="player"
              :team="selectedTeam"
              :isPlaceholder="player.id < 0"
              :showResetButton="false"
              :isOriginalPlayer="false"
              is-draft-simulator
            ></card-player-squad-simulator>
            <card-player-squad-simulator
              v-for="i in MAX_PLAYERS - modifiedTeamPlayers.length"
              :key="i"
              :player="placeholderPlayer"
              :team="selectedTeam"
              isPlaceholder
              is-draft-simulator
            ></card-player-squad-simulator>
          </div>
        </div>
      </div>
    </transition>
    <BaseModal
      v-model="modalInfo.visible"
      modal-id="modalSquadSimulator"
      :message="modalInfo.message"
      isAlert
      showCloseButton
    />

    <!-- Instruction Section -->
    <div id="instructionsCard" class="kql-w-container py-2 px-4 md:py-3 md:px-6">
      <div class="bg-gray-800 rounded-lg p-6">
        <div class="flex items-center justify-center gap-4">
          <p class="text-2xl font-bold text-center mb-4">
            {{ t("squadSimulator.instructions.title") }}
          </p>
          <a
            v-if="selectedTeam"
            href="#teamsGrid"
            class="bg-primary text-black rounded-full font-bold p-1.5 mr-2 max-w-max -mt-5"
          >
            <left-arrow :width="8" :height="8" class="rotate-90" />
          </a>
        </div>

        <ol class="list-decimal md:pl-6 space-y-2">
          <li class="flex items-center space-x-3">
            <span class="text-xl font-bold text-primary">1</span>
            <p class="text-base">{{ t("squadSimulator.instructions.step1") }}</p>
          </li>

          <li class="flex items-center space-x-3">
            <span class="text-xl font-bold text-primary">2</span>
            <div>
              <p class="text-base">{{ t("squadSimulator.team.instructions.step2") }}</p>
            </div>
          </li>

          <!-- Step 3 -->
          <li class="flex items-center space-x-3">
            <span class="text-xl font-bold text-primary">3</span>
            <div class="flex gap-2">
              <i18n-t keypath="squadSimulator.instructions.step3" tag="p" class="text-base">
                <template #hashtag>
                  <span class="font-bold text-primary">#{{ contextSiteTitleNoSpaces }}Simulator</span>
                </template>
              </i18n-t>
            </div>
          </li>
        </ol>
      </div>
    </div>
  </section>

  <!--  Download Image-->
  <BaseModal
    isPreview
    modalId="download-image-modal"
    :modelValue="showModal"
    @update:modelValue="showModal = $event"
  >
    <!-- Modal Body -->
    <template #body>
      <!-- Orientation Selection -->
      <div class="flex justify-center gap-4 py-4">
        <div
          :class="{
            'border-b-2 border-primary text-primary font-bold': !isPortrait,
            'text-gray-500': isPortrait,
          }"
          @click="isPortrait = false"
          class="cursor-pointer px-4 py-2 transition-colors"
        >
          {{ t("squadSimulator.orientation.landscape") }}
        </div>
        <div
          :class="{
            'border-b-2 border-primary text-primary font-bold': isPortrait,
            'text-gray-500': !isPortrait,
          }"
          @click="isPortrait = true"
          class="cursor-pointer px-4 py-2 transition-colors"
        >
          {{ t("squadSimulator.orientation.portrait") }}
        </div>
      </div>

      <!-- Preview Image based on Orientation -->
      <section
        class="origin-top-left scale-[0.25] h-[380px] w-[380px]"
        :class="{
          '-ml-2': !isPortrait,
          'pl-40': isPortrait,
        }"
      >
        <div
          id="SquadSimulatorCapture"
          class="flex flex-col pt-8 px-8 pb-10"
          :class="{
            'w-[1200px] h-[800px]': !isPortrait,
            'w-[800px] h-[1200px]': isPortrait,
          }"
        >
          <div class="flex justify-between items-center gap-4 px-6 pt-6">
            <div>
              <!--              Context League Logo-->
              <img
                :src="currentImage.src"
                :alt="currentImage.alt"
                :width="currentImage.width"
                :height="currentImage.height"
              />
            </div>
            <div class="text-right">
              <p class="text-xl font-bold">
                {{ t("squadSimulator.capture.text1") }}
                <span class="ml-3">🔥💪</span>
              </p>
              <p class="text-lg font-bold">{{ t("squadSimulator.capture.text2", { contextSiteTitle }) }}</p>
              <p>{{ t("squadSimulator.capture.text3") }}</p>
              <p class="text-end">
                <span class="font-bold text-primary">#{{ contextSiteTitleNoSpaces }}Simulator</span>
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-2 px-4 md:px-8 py-6 mx-auto">
            <!-- Selected Team -->
            <card-team-squad-simulator
              v-if="selectedTeam"
              isDownload
              :key="selectedTeam.id"
              :team="selectedTeam"
              :teamBudget="undefined"
              v-model:selected-team="selectedTeam"
            ></card-team-squad-simulator>

            <div class="flex flex-wrap justify-center gap-4">
              <!-- Modified Team Players -->
              <card-player-squad-simulator
                v-for="player in downloadPlayers"
                :key="player.id"
                :player="player"
                :team="selectedTeam"
                isDownload
                is-draft-simulator
              />
            </div>
          </div>
        </div>
      </section>
      <i18n-t keypath="squadSimulator.capture.share" tag="p" class="text-end px-4 pb-4 -mt-4">
        <template #hashtag>
          <span class="font-bold text-primary">#{{ contextSiteTitleNoSpaces }}Simulator</span>
        </template>
      </i18n-t>
    </template>

    <!-- Modal Footer -->
    <template #footer>
      <div class="flex items-center justify-evenly gap-4 mb-6 -mt-8">
        <button class="btn btn-muted" @click="showModal = false">
          {{ t("squadSimulator.button.cancel") }}
        </button>
        <button
          class="btn flex items-center justify-center gap-2 md:justify-start"
          :class="isDownloading ? 'btn-muted' : 'btn-emphasis'"
          @click="downloadTeamImage"
        >
          <BaseSpinner class="!fill-black" :size="25" v-if="isDownloading" />
          {{ t("squadSimulator.button.confirm") }}
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<style scoped>
.teams-enter-active,
.teams-leave-active,
.teams-move {
  transition: all 0.6s ease;
}

.teams-enter-from,
.teams-leave-from {
  transform: translateY(-10px);
  opacity: 0;
}

.teams-leave-active {
  position: absolute;
}

.squad-enter-active,
.squad-leave-active {
  transition: all 0.8s ease;
}

.squad-enter-from,
.squad-leave-from {
  opacity: 0;
}

.kql-player-filter {
  @apply px-4 py-2 my-1 lg:my-0 text-sm rounded-full lg:rounded-none border-gray-700 border lg:border-t-0 lg:border-b-0;
  @apply lg:border-l lg:border-r bg-gray-900 lg:border-gray-800 text-gray-400 hover:text-black hover:bg-primary w-full lg:w-auto;
}

.kql-player-filter:first-child {
  @apply lg:rounded-s-lg lg:border-l-0;
}

.kql-player-filter:last-child {
  @apply lg:rounded-e-lg lg:border-r-0 lg:rounded-s-none;
}

.range_container.range-average > p {
  @apply !mb-4;
}

.swing {
  position: relative;
  animation: swing 5s 5 ease-in-out;
}

@keyframes swing {
  18% {
    transform: translateX(0);
  }
  24% {
    transform: translateX(-20px);
  }
  30% {
    transform: translateX(0);
  }
  36% {
    transform: translateX(-20px);
  }
  42% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
